import React from "react";
import { Box, Divider, Skeleton, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { formatFundsUsd, PERIOD_TOTAL_LABELS } from "../../Utils/fundsPeriodTotals";

const SegmentColumn = ({ title, icon, accent, data, loading, txnLabel }) => (
  <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.35, sm: 1.6 }, py: 1.35 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.65, mb: 0.75 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${accent}18`,
          color: accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ color: "#c8c8c8", fontSize: 11.5, fontWeight: 750, letterSpacing: 0.25 }}>
        {title}
      </Typography>
    </Box>
    {loading ? (
      <Skeleton width="70%" height={26} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
    ) : (
      <Typography
        sx={{
          color: "#fff",
          fontSize: { xs: 18, sm: 19 },
          fontWeight: 800,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
          mb: 0.25,
        }}
      >
        {formatFundsUsd(data?.total || 0)}
      </Typography>
    )}
    {!loading && (
      <Typography sx={{ color: "#777", fontSize: 11, fontWeight: 500 }}>
        {data?.count || 0} {txnLabel}
      </Typography>
    )}
  </Box>
);

const FundsPeriodTotalsBar = ({
  totals,
  loading = false,
  type = "deposit",
  startDate,
  endDate,
  labels: labelsOverride,
}) => {
  const labels = labelsOverride || PERIOD_TOTAL_LABELS[type] || PERIOD_TOTAL_LABELS.deposit;
  const label = labels.periodHeader;
  const txnLabel = labels.txnLabel;
  const periodLabel =
    startDate && endDate
      ? `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`
      : "";

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        borderRadius: "14px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        background: "linear-gradient(135deg, rgba(12, 44, 80, 0.55) 0%, rgba(20, 20, 20, 0.96) 48%, rgba(10, 10, 10, 0.98) 100%)",
        overflow: "hidden",
        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.28)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.9,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
          bgcolor: "rgba(0, 0, 0, 0.24)",
        }}
      >
        <CalendarMonthIcon sx={{ fontSize: 15, color: "#2AF598", opacity: 0.9 }} />
        <Typography
          sx={{
            color: "#bdbdbd",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            lineHeight: 1.3,
          }}
        >
          {label}
          {periodLabel ? ` · ${periodLabel}` : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        <SegmentColumn
          title="Individuals"
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          accent="#2AF598"
          data={totals?.individual}
          loading={loading}
          txnLabel={txnLabel}
        />
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: "rgba(255, 255, 255, 0.08)", my: 1.25 }}
        />
        <SegmentColumn
          title="Corporate"
          icon={<BusinessIcon sx={{ fontSize: 16 }} />}
          accent="#46adf2"
          data={totals?.corporate}
          loading={loading}
          txnLabel={txnLabel}
        />
      </Box>
    </Box>
  );
};

export default FundsPeriodTotalsBar;
